import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useState } from 'react';
import { useGetRoutesQuery } from '../services/routes.service';
import { RoutesFilter } from '../models';
import { RouteStatus } from '../enums';
import { routesFilterForApiQuery } from '../utils/buildAppliedRoutesFilterFromLocal';

const PENDING_HERMES_POLLING_INTERVAL_MS = 7000;

interface UseRoutesProps {
  filters?: RoutesFilter;
  page: number;
  pageSize: number;
  skip?: boolean;
}

const useRoutes = ({
  filters,
  page,
  pageSize,
  skip = false,
}: UseRoutesProps) => {
  const [pollingInterval, setPollingInterval] = useState(0);

  const filtersForApi = useMemo(
    () => routesFilterForApiQuery(filters),
    [filters]
  );

  const isMissingDates =
    !filtersForApi?.routeId?.trim() &&
    (!filtersForApi?.scheduledFrom || !filtersForApi?.scheduledTo);
  const shouldSkip = skip || isMissingDates;

  const { data, isLoading, error } = useGetRoutesQuery(
    shouldSkip
      ? skipToken
      : {
          paginated: { page, pageSize },
          filters: filtersForApi,
        },
    {
      pollingInterval,
      skipPollingIfUnfocused: true,
    }
  );

  const count = data?.count ?? 0;
  const rows = data?.items ?? [];

  useEffect(() => {
    if (skip) {
      if (pollingInterval !== 0) setPollingInterval(0);
      return;
    }

    const hasPendingHermes = rows.some(
      (r) => r.status === RouteStatus.PENDING_HERMES_ASSIGN
    );

    const nextInterval = hasPendingHermes
      ? PENDING_HERMES_POLLING_INTERVAL_MS
      : 0;

    if (nextInterval !== pollingInterval) {
      setPollingInterval(nextInterval);
    }
  }, [rows, skip, pollingInterval]);

  return {
    routes: rows,
    totalItems: count,
    totalPages: count ? Math.ceil(count / pageSize) : 0,
    isLoading,
    error,
  };
};

export default useRoutes;
